import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getSelectedUtility } from '../../utils/utilitySelection';
import { formatUtilityDisplayName } from '../../utils/formatUtilityName';

function UtilityPlanningDashboard() {
        const [cardRef, isCardVisible] = useScrollAnimation({ threshold: 0.2, triggerOnce: true });
    const [selectedUtility, setSelectedUtilityState] = useState(null);

    useEffect(() => {
        setSelectedUtilityState(getSelectedUtility());
    }, []);

    const selectedUtilityDisplay = useMemo(() => {
        const name = selectedUtility?.utilityName;
        if (!name) return '';
        return formatUtilityDisplayName(name);
    }, [selectedUtility]);

    
    return (
        <div className="min-h-screen bg-gray-50">
        <div className="p-8 max-w-7xl mx-auto">
          <div
            ref={cardRef}
            className={`bg-white rounded-xl shadow-lg p-10 mt-[5vh] transition-all duration-1000 ease-out transform ${
              isCardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="w-full text-center text-4xl font-bold text-gray-800 pb-6 font-ui">
              Welcome to GridPlan
            </h1>

            {selectedUtilityDisplay ? (
              <div className="flex justify-center pb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-800 border border-blue-100 font-ui">
                  {/* <span className="text-sm">Planning for</span> */}
                  <span className="text-sm font-semibold">{selectedUtilityDisplay}</span>
                </div>
              </div>
            ) : (
              <div className="text-center pb-8 font-ui text-sm text-gray-600">
                No utility selected yet.{' '}
                <Link to="/utility/select" className="text-blue-700 hover:underline font-semibold">
                  Select a utility
                </Link>
                .
              </div>
            )}
            
            <p className="text-lg text-gray-700 leading-relaxed pb-5 font-ui">
              GridPlan is your comprehensive tool for EV load forecasting and grid capacity analysis. Analyze EV adoption scenarios, forecast load growth, and understand future grid demand with advanced analytics and visualization tools.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed pb-5 font-ui">
              Everything you need to understand EV load impacts on your grid is in one place. Grid capacity analysis, load forecasting, demand growth projections, and more are all available so you can see the complete picture of future grid utilization.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed pb-5 font-ui">
              How to run an analysis:
            </p>
            
            <ol className="list-decimal pl-6 mb-6 space-y-3 font-ui text-lg text-gray-700 leading-relaxed">
              <li>Go to <strong>Workflow</strong> in the sidebar.</li>
              <li>Select the <strong>vehicle type(s)</strong> you want to analyze (e.g. Passenger EV, MDHD Trucks, Transit Bus).</li>
              <li>Configure <strong>scenario inputs</strong> (rate plan, managed charging, vehicle pricing, policy, charging infra, and more) for your case.</li>
              <li>Click <strong>Start analysis</strong> to run the analysis; results appear under Scenario Results.</li>
            </ol>

            <p className="text-lg text-gray-700 leading-relaxed pb-5 font-ui">
              Ready to get started? Open <strong>Workflow</strong> to select vehicle types, set your scenario, and run an analysis.
            </p>

            <div className="flex justify-center mt-8">
              <Link
                to="/utility/workflow"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 font-ui"
              >
                Go to Workflow
              </Link>
            </div>
          </div>
        </div>
    </div>
    );
}

export default UtilityPlanningDashboard;
